import {
    AlignmentType,
    BorderStyle,
    Document,
    Footer,
    Header,
    ImageRun,
    Packer,
    Paragraph,
    ShadingType,
    SimpleField,
    Table,
    TableCell,
    TableLayoutType,
    TableRow,
    TextRun,
    VerticalAlignTable,
    WidthType,
} from 'docx';
import type { ReportEvent, ReportWithEvents } from '@/types/report';
import { hasRtlText } from '@/utils/bidi-text';
import { formatDate } from '@/utils/date';
import { triggerBlobDownload } from '@/utils/download';
import { toSafeExportFileName } from '@/utils/file-name';
import {
    buildReportExportGroupSummaryParts,
    buildReportExportSummaryGrid,
    formatReportExportEventTimestamp,
    groupReportEventsForExport,
    loadReportExportLogoPngForDocx,
    resolveReportExportCreatedByLabel,
    resolveReportExportEventContextColumn,
    type ReportExportSummaryCell,
} from './reportExportLayout';
import {
    ACCENT,
    REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
    fetchImageBytes,
    LEFT_COL_RATIO,
    MUTED,
    PAGE_RULE,
    SURFACE,
    TABLE_HEADER_BG,
    TEXT,
} from './reportExportShared';
import type { ReportGroupByMode } from '../constants';
import type { ReportExportContext } from '../hooks/useReportExport';

const THEME = {
    accent: ACCENT.replace('#', ''),
    text: TEXT.replace('#', ''),
    muted: MUTED.replace('#', ''),
    tableHeader: TABLE_HEADER_BG.replace('#', ''),
    surface: SURFACE.replace('#', ''),
    pageRule: PAGE_RULE.replace('#', ''),
};

const WORD_EXPORT_PAGE_TWIP = {
    width: 12240,
    height: 15840,
    marginTop: 2160,
    marginRight: 1080,
    marginBottom: 1440,
    marginLeft: 1080,
    header: 1020,
    footer: 480,
} as const;

const WORD_EXPORT_BODY_WIDTH_TWIP =
    WORD_EXPORT_PAGE_TWIP.width -
    WORD_EXPORT_PAGE_TWIP.marginLeft -
    WORD_EXPORT_PAGE_TWIP.marginRight;

const EVENT_ROW_LEFT_CELL_MARGIN_RIGHT_TWIP = 140;
const SECONDARY_THUMB_TABLE_CELL_HORIZONTAL_MARGIN_TWIP = 80;
const SUMMARY_PANEL_PAD_TWIP = 360;
const THUMBNAIL_CELL_INSET_TWIP = 108;

const HEADER_COL_TWIP = WORD_EXPORT_BODY_WIDTH_TWIP / 2;
const SUMMARY_COL_TWIP =
    (WORD_EXPORT_BODY_WIDTH_TWIP - SUMMARY_PANEL_PAD_TWIP * 2) / 3;
const LEFT_COL_TWIP = Math.round(WORD_EXPORT_BODY_WIDTH_TWIP * LEFT_COL_RATIO);
const RIGHT_COL_TWIP = WORD_EXPORT_BODY_WIDTH_TWIP - LEFT_COL_TWIP;
const EVENT_LEFT_PANEL_WIDTH_TWIP =
    LEFT_COL_TWIP - EVENT_ROW_LEFT_CELL_MARGIN_RIGHT_TWIP;
const EVENT_COL_EVENT_TWIP = Math.round(EVENT_LEFT_PANEL_WIDTH_TWIP * 0.55);
const EVENT_COL_SOURCE_TWIP =
    EVENT_LEFT_PANEL_WIDTH_TWIP - EVENT_COL_EVENT_TWIP;

const fixedTableLayoutOptions = (columnWidthsTwip: number[]) => ({
    width: {
        size: columnWidthsTwip.reduce((total, width) => total + width, 0),
        type: WidthType.DXA,
    },
    columnWidths: columnWidthsTwip,
    layout: TableLayoutType.FIXED,
});

const twipCellWidth = (twip: number) => ({
    size: twip,
    type: WidthType.DXA,
});

const wordExportTwipToLayoutPx = (twip: number): number =>
    Math.round((twip * 96) / (72 * 20));

const wordExportEventLeftPanelInnerWidthPx = (): number =>
    Math.max(160, wordExportTwipToLayoutPx(EVENT_LEFT_PANEL_WIDTH_TWIP));

const wordSecondaryThumbnailRasterBounds = (
    thumbCount: number,
): { maxWidth: number; maxHeight: number } => {
    const panelInnerPx = wordExportEventLeftPanelInnerWidthPx();
    const reservedPx = 6;
    const cellHorizontalInsetPx = wordExportTwipToLayoutPx(
        SECONDARY_THUMB_TABLE_CELL_HORIZONTAL_MARGIN_TWIP,
    );
    const perThumbPx =
        Math.floor((panelInnerPx - reservedPx) / Math.max(1, thumbCount)) -
        cellHorizontalInsetPx;
    const maxWidth = Math.max(48, Math.min(perThumbPx, 176));
    const maxHeight = Math.max(28, Math.round((maxWidth * 9) / 16));
    return { maxWidth, maxHeight };
};

const CELL_PAD = { top: 100, bottom: 100, left: 120, right: 120 };

const ruleBorder = {
    style: BorderStyle.SINGLE,
    size: 6,
    color: THEME.pageRule,
} as const;

const blankTableBorder = {
    style: BorderStyle.NONE,
    size: 0,
} as const;

const invisibleTableOutline = {
    top: blankTableBorder,
    bottom: blankTableBorder,
    left: blankTableBorder,
    right: blankTableBorder,
    insideHorizontal: blankTableBorder,
    insideVertical: blankTableBorder,
} as const;

const inferImageType = (bytes: Uint8Array): 'jpg' | 'png' | 'gif' | null => {
    if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) {
        return 'jpg';
    }
    if (
        bytes.length >= 4 &&
        bytes[0] === 0x89 &&
        bytes[1] === 0x50 &&
        bytes[2] === 0x4e &&
        bytes[3] === 0x47
    ) {
        return 'png';
    }
    if (
        bytes.length >= 6 &&
        bytes[0] === 0x47 &&
        bytes[1] === 0x49 &&
        bytes[2] === 0x46
    ) {
        return 'gif';
    }
    return null;
};

const mimeTypeForWordExportImageType = (
    imageType: 'jpg' | 'png' | 'gif',
): string => {
    if (imageType === 'jpg') {
        return 'image/jpeg';
    }
    if (imageType === 'png') {
        return 'image/png';
    }
    return 'image/gif';
};

const wordRasterTransformationPreservingAspectRatio = async (
    bytes: Uint8Array,
    imageType: 'jpg' | 'png' | 'gif',
    maxWidth: number,
    maxHeight: number,
): Promise<{ width: number; height: number }> => {
    const mime = mimeTypeForWordExportImageType(imageType);
    try {
        const rasterCopy = new Uint8Array(bytes.length);
        rasterCopy.set(bytes);
        const bitmap = await createImageBitmap(
            new Blob([rasterCopy], { type: mime }),
        );
        const pixelWidth = bitmap.width;
        const pixelHeight = bitmap.height;
        bitmap.close();
        if (pixelWidth > 0 && pixelHeight > 0) {
            const scale = Math.min(
                maxWidth / pixelWidth,
                maxHeight / pixelHeight,
                1,
            );
            return {
                width: Math.max(1, Math.round(pixelWidth * scale)),
                height: Math.max(1, Math.round(pixelHeight * scale)),
            };
        }
    } catch {}
    return {
        width: maxWidth,
        height: Math.max(1, Math.round((maxWidth * 9) / 16)),
    };
};

const buildReportDocumentHeader = (
    logo: {
        data: Uint8Array;
        transformWidth: number;
        transformHeight: number;
    } | null,
): Header => {
    const logoParagraph = logo
        ? new Paragraph({
              children: [
                  new ImageRun({
                      type: 'png',
                      data: logo.data,
                      transformation: {
                          width: logo.transformWidth,
                          height: logo.transformHeight,
                      },
                  }),
              ],
          })
        : new Paragraph({
              children: [
                  new TextRun({
                      text: 'Conntour',
                      bold: true,
                      size: 24,
                      font: 'Georgia',
                      color: THEME.accent,
                  }),
              ],
          });

    const headerTable = new Table({
        ...fixedTableLayoutOptions([HEADER_COL_TWIP, HEADER_COL_TWIP]),
        borders: {
            top: blankTableBorder,
            left: blankTableBorder,
            right: blankTableBorder,
            bottom: ruleBorder,
            insideHorizontal: blankTableBorder,
            insideVertical: blankTableBorder,
        },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(HEADER_COL_TWIP),
                        verticalAlign: VerticalAlignTable.CENTER,
                        margins: { top: 60, bottom: 100, left: 0, right: 120 },
                        children: [logoParagraph],
                    }),
                    new TableCell({
                        width: twipCellWidth(HEADER_COL_TWIP),
                        verticalAlign: VerticalAlignTable.CENTER,
                        margins: { top: 60, bottom: 100, left: 0, right: 0 },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.END,
                                children: [
                                    new TextRun({
                                        text: 'Page ',
                                        size: 22,
                                        font: 'Arial',
                                        color: THEME.muted,
                                    }),
                                    new SimpleField('PAGE'),
                                    new TextRun({
                                        text: ' / ',
                                        size: 22,
                                        font: 'Arial',
                                        color: THEME.muted,
                                    }),
                                    new SimpleField('NUMPAGES'),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });

    return new Header({
        children: [headerTable],
    });
};

const buildReportDocumentFooter = (generatedAt: string): Footer =>
    new Footer({
        children: [
            new Paragraph({
                alignment: AlignmentType.END,
                spacing: { before: 80 },
                children: [
                    new TextRun({
                        text: `Generated ${generatedAt}`,
                        italics: true,
                        size: 18,
                        font: 'Arial',
                        color: THEME.muted,
                    }),
                ],
            }),
        ],
    });

const userTextRunOptions = (text: string) =>
    hasRtlText(text) ? { text, rightToLeft: true } : { text };

const buildTitleBlock = (report: ReportWithEvents): (Paragraph | Table)[] => {
    const description = report.description?.trim() || report.name;
    return [
        new Paragraph({
            spacing: { before: 80, after: 80 },
            bidirectional: hasRtlText(report.name),
            children: [
                new TextRun({
                    ...userTextRunOptions(report.name),
                    bold: true,
                    size: 56,
                    font: 'Georgia',
                    color: THEME.text,
                }),
            ],
        }),
        new Paragraph({
            spacing: { after: 240 },
            bidirectional: hasRtlText(description),
            children: description.split('\n').map(
                (segment, index) =>
                    new TextRun({
                        ...userTextRunOptions(segment),
                        break: index > 0 ? 1 : undefined,
                        size: 30,
                        font: 'Georgia',
                        color: THEME.muted,
                    }),
            ),
        }),
    ];
};

const buildSummaryPanelTable = (grid: ReportExportSummaryCell[][]): Table =>
    new Table({
        ...fixedTableLayoutOptions([WORD_EXPORT_BODY_WIDTH_TWIP]),
        borders: invisibleTableOutline,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(WORD_EXPORT_BODY_WIDTH_TWIP),
                        shading: {
                            fill: THEME.surface,
                            type: ShadingType.CLEAR,
                        },
                        margins: {
                            top: SUMMARY_PANEL_PAD_TWIP,
                            bottom: SUMMARY_PANEL_PAD_TWIP,
                            left: SUMMARY_PANEL_PAD_TWIP,
                            right: SUMMARY_PANEL_PAD_TWIP,
                        },
                        children: [
                            new Table({
                                ...fixedTableLayoutOptions([
                                    SUMMARY_COL_TWIP,
                                    SUMMARY_COL_TWIP,
                                    SUMMARY_COL_TWIP,
                                ]),
                                borders: invisibleTableOutline,
                                rows: grid.map(
                                    (row) =>
                                        new TableRow({
                                            children: row.map(
                                                (cell) =>
                                                    new TableCell({
                                                        width: twipCellWidth(
                                                            SUMMARY_COL_TWIP,
                                                        ),
                                                        margins: CELL_PAD,
                                                        children: [
                                                            new Paragraph({
                                                                children: [
                                                                    new TextRun(
                                                                        {
                                                                            text: cell.label,
                                                                            bold: true,
                                                                            size: 20,
                                                                            font: 'Arial',
                                                                            color: THEME.accent,
                                                                        },
                                                                    ),
                                                                ],
                                                            }),
                                                            new Paragraph({
                                                                spacing: {
                                                                    before: 40,
                                                                },
                                                                bidirectional:
                                                                    hasRtlText(
                                                                        cell.value,
                                                                    ),
                                                                children: [
                                                                    new TextRun(
                                                                        {
                                                                            ...userTextRunOptions(
                                                                                cell.value,
                                                                            ),
                                                                            bold: true,
                                                                            size: 24,
                                                                            font: 'Arial',
                                                                            color: THEME.text,
                                                                        },
                                                                    ),
                                                                ],
                                                            }),
                                                        ],
                                                    }),
                                            ),
                                        }),
                                ),
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });

const buildDividerParagraph = (): Paragraph =>
    new Paragraph({
        spacing: { after: 160 },
        border: {
            bottom: ruleBorder,
        },
        children: [new TextRun({ text: '\u00a0', size: 4 })],
    });

const buildPromptIntroParagraphs = (
    headingLabel: string,
    promptText: string,
    events: ReportEvent[],
): Paragraph[] => {
    const body = (promptText || 'Untitled group').trim() || 'Untitled group';
    const summaryParts = buildReportExportGroupSummaryParts(events);
    return [
        new Paragraph({
            spacing: { before: 280, after: 120 },
            bidirectional: hasRtlText(body),
            children: [
                new TextRun({
                    text: `${headingLabel}  `,
                    bold: true,
                    size: 28,
                    font: 'Georgia',
                    color: THEME.text,
                }),
                new TextRun({
                    ...userTextRunOptions(body),
                    size: 40,
                    font: 'Georgia',
                    color: THEME.muted,
                }),
            ],
        }),
        new Paragraph({
            spacing: { after: 280 },
            children: [
                new TextRun({
                    text: summaryParts.join(' • '),
                    size: 22,
                    font: 'Arial',
                    color: THEME.muted,
                }),
            ],
        }),
    ];
};

const buildEventDataTable = (
    event: ReportEvent,
    groupBy: ReportGroupByMode,
): Table => {
    const contextColumn = resolveReportExportEventContextColumn(event, groupBy);
    const description = event.description?.trim();
    return new Table({
        ...fixedTableLayoutOptions([
            EVENT_COL_EVENT_TWIP,
            EVENT_COL_SOURCE_TWIP,
        ]),
        borders: invisibleTableOutline,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(EVENT_COL_EVENT_TWIP),
                        shading: {
                            fill: THEME.tableHeader,
                            type: ShadingType.CLEAR,
                        },
                        margins: CELL_PAD,
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: 'Event',
                                        bold: true,
                                        size: 22,
                                        font: 'Arial',
                                        color: THEME.text,
                                    }),
                                ],
                            }),
                        ],
                    }),
                    new TableCell({
                        width: twipCellWidth(EVENT_COL_SOURCE_TWIP),
                        shading: {
                            fill: THEME.tableHeader,
                            type: ShadingType.CLEAR,
                        },
                        margins: CELL_PAD,
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: contextColumn.label,
                                        bold: true,
                                        size: 22,
                                        font: 'Arial',
                                        color: THEME.text,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(EVENT_COL_EVENT_TWIP),
                        margins: CELL_PAD,
                        verticalAlign: VerticalAlignTable.TOP,
                        children: [
                            new Paragraph({
                                children: [
                                    new TextRun({
                                        text: formatReportExportEventTimestamp(
                                            event,
                                        ),
                                        bold: true,
                                        size: 26,
                                        font: 'Arial',
                                        color: THEME.text,
                                    }),
                                ],
                            }),
                            ...(description
                                ? [
                                      new Paragraph({
                                          spacing: { before: 80 },
                                          bidirectional:
                                              hasRtlText(description),
                                          children: [
                                              new TextRun({
                                                  ...userTextRunOptions(
                                                      description,
                                                  ),
                                                  size: 22,
                                                  font: 'Arial',
                                                  color: THEME.muted,
                                              }),
                                          ],
                                      }),
                                  ]
                                : []),
                        ],
                    }),
                    new TableCell({
                        width: twipCellWidth(EVENT_COL_SOURCE_TWIP),
                        margins: CELL_PAD,
                        verticalAlign: VerticalAlignTable.TOP,
                        children: [
                            new Paragraph({
                                bidirectional: hasRtlText(contextColumn.value),
                                children: [
                                    new TextRun({
                                        ...userTextRunOptions(
                                            contextColumn.value,
                                        ),
                                        size: 24,
                                        font: 'Arial',
                                        color: THEME.text,
                                    }),
                                ],
                            }),
                            ...(contextColumn.detail
                                ? [
                                      new Paragraph({
                                          spacing: { before: 80 },
                                          bidirectional: hasRtlText(
                                              contextColumn.detail,
                                          ),
                                          children: [
                                              new TextRun({
                                                  ...userTextRunOptions(
                                                      contextColumn.detail,
                                                  ),
                                                  size: 18,
                                                  font: 'Arial',
                                                  color: THEME.muted,
                                              }),
                                          ],
                                      }),
                                  ]
                                : []),
                        ],
                    }),
                ],
            }),
        ],
    });
};

const buildEventSecondaryThumbnailsRowTable = async (
    event: ReportEvent,
): Promise<Table | null> => {
    const secondaryUrls = event.thumbnailUrls.slice(
        1,
        1 + REPORT_EXPORT_EXTRA_THUMBNAIL_FRAMES,
    );
    if (secondaryUrls.length === 0) {
        return null;
    }
    const secondaryResults = await Promise.all(
        secondaryUrls.map(async (url) => {
            const bytes = await fetchImageBytes(url);
            const type = bytes ? inferImageType(bytes) : null;
            return bytes && type ? { data: bytes, type } : null;
        }),
    );
    const validResults = secondaryResults.filter(
        (r): r is { data: Uint8Array; type: 'jpg' | 'png' | 'gif' } =>
            r !== null,
    );
    if (validResults.length === 0) {
        return null;
    }
    const rasterBounds = wordSecondaryThumbnailRasterBounds(
        validResults.length,
    );
    const resultsWithTransform = await Promise.all(
        validResults.map(async (result) => ({
            ...result,
            transformation: await wordRasterTransformationPreservingAspectRatio(
                result.data,
                result.type,
                rasterBounds.maxWidth,
                rasterBounds.maxHeight,
            ),
        })),
    );
    const baseWidth = Math.floor(
        EVENT_LEFT_PANEL_WIDTH_TWIP / resultsWithTransform.length,
    );
    const remainder =
        EVENT_LEFT_PANEL_WIDTH_TWIP - baseWidth * resultsWithTransform.length;
    const columnWidths = resultsWithTransform.map(
        (_, index) => baseWidth + (index < remainder ? 1 : 0),
    );
    return new Table({
        ...fixedTableLayoutOptions(columnWidths),
        borders: invisibleTableOutline,
        rows: [
            new TableRow({
                children: resultsWithTransform.map(
                    (result, index) =>
                        new TableCell({
                            width: twipCellWidth(columnWidths[index]),
                            margins: {
                                top: 60,
                                bottom: 60,
                                left: 40,
                                right: 40,
                            },
                            borders: {
                                top: ruleBorder,
                                bottom: ruleBorder,
                                left: ruleBorder,
                                right: ruleBorder,
                            },
                            verticalAlign: VerticalAlignTable.CENTER,
                            shading: {
                                fill: THEME.surface,
                                type: ShadingType.CLEAR,
                            },
                            children: [
                                new Paragraph({
                                    alignment: AlignmentType.CENTER,
                                    children: [
                                        new ImageRun({
                                            type: result.type,
                                            data: result.data,
                                            transformation:
                                                result.transformation,
                                        }),
                                    ],
                                }),
                            ],
                        }),
                ),
            }),
        ],
    });
};

const buildEventLeftPanel = async (
    event: ReportEvent,
    groupBy: ReportGroupByMode,
): Promise<Table> => {
    const dataTable = buildEventDataTable(event, groupBy);
    const secondaryTable = await buildEventSecondaryThumbnailsRowTable(event);
    const rows: TableRow[] = [
        new TableRow({
            children: [
                new TableCell({
                    width: twipCellWidth(EVENT_LEFT_PANEL_WIDTH_TWIP),
                    margins: { top: 0, bottom: 0, left: 0, right: 0 },
                    children: [dataTable],
                }),
            ],
        }),
    ];
    if (secondaryTable) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(EVENT_LEFT_PANEL_WIDTH_TWIP),
                        margins: {
                            top: 100,
                            bottom: 80,
                            left: 0,
                            right: 0,
                        },
                        borders: {
                            top: ruleBorder,
                        },
                        children: [secondaryTable],
                    }),
                ],
            }),
        );
    }
    return new Table({
        ...fixedTableLayoutOptions([EVENT_LEFT_PANEL_WIDTH_TWIP]),
        borders: invisibleTableOutline,
        rows,
    });
};

const buildThumbnailPanel = async (
    event: ReportEvent,
): Promise<(Paragraph | Table)[]> => {
    const imageBytes = await fetchImageBytes(event.thumbnailUrls[0]);
    const imageType = imageBytes ? inferImageType(imageBytes) : null;

    const innerChildren: (Paragraph | Table)[] = [];

    if (imageBytes && imageType) {
        const maxWidth = wordExportTwipToLayoutPx(
            RIGHT_COL_TWIP - THUMBNAIL_CELL_INSET_TWIP * 2,
        );
        const maxHeight = Math.round((maxWidth * 9) / 16);
        innerChildren.push(
            new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                    new ImageRun({
                        type: imageType,
                        data: imageBytes,
                        transformation:
                            await wordRasterTransformationPreservingAspectRatio(
                                imageBytes,
                                imageType,
                                maxWidth,
                                maxHeight,
                            ),
                    }),
                ],
            }),
        );
    } else {
        innerChildren.push(
            new Paragraph({
                alignment: AlignmentType.CENTER,
                spacing: { before: 2000, after: 2000 },
                children: [
                    new TextRun({
                        text: 'No preview image',
                        size: 22,
                        font: 'Arial',
                        color: THEME.accent,
                    }),
                ],
            }),
        );
    }

    return [
        new Table({
            ...fixedTableLayoutOptions([RIGHT_COL_TWIP]),
            borders: invisibleTableOutline,
            rows: [
                new TableRow({
                    children: [
                        new TableCell({
                            width: twipCellWidth(RIGHT_COL_TWIP),
                            verticalAlign: VerticalAlignTable.CENTER,
                            shading: {
                                fill: THEME.surface,
                                type: ShadingType.CLEAR,
                            },
                            children: innerChildren,
                        }),
                    ],
                }),
            ],
        }),
    ];
};

const buildPdfLikeEventRow = async (
    event: ReportEvent,
    groupBy: ReportGroupByMode,
): Promise<Table> => {
    const leftPanel = await buildEventLeftPanel(event, groupBy);
    const thumbBlocks = await buildThumbnailPanel(event);

    return new Table({
        ...fixedTableLayoutOptions([LEFT_COL_TWIP, RIGHT_COL_TWIP]),
        borders: invisibleTableOutline,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(LEFT_COL_TWIP),
                        verticalAlign: VerticalAlignTable.TOP,
                        margins: {
                            top: 0,
                            bottom: 0,
                            left: 0,
                            right: EVENT_ROW_LEFT_CELL_MARGIN_RIGHT_TWIP,
                        },
                        children: [leftPanel],
                    }),
                    new TableCell({
                        width: twipCellWidth(RIGHT_COL_TWIP),
                        verticalAlign: VerticalAlignTable.TOP,
                        margins: { top: 0, bottom: 0, left: 0, right: 0 },
                        children: thumbBlocks,
                    }),
                ],
            }),
        ],
    });
};

const buildEmptyEventsPanel = (): Table =>
    new Table({
        ...fixedTableLayoutOptions([WORD_EXPORT_BODY_WIDTH_TWIP]),
        borders: invisibleTableOutline,
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        width: twipCellWidth(WORD_EXPORT_BODY_WIDTH_TWIP),
                        shading: {
                            fill: THEME.surface,
                            type: ShadingType.CLEAR,
                        },
                        margins: {
                            top: 720,
                            bottom: 720,
                            left: 360,
                            right: 360,
                        },
                        children: [
                            new Paragraph({
                                alignment: AlignmentType.CENTER,
                                children: [
                                    new TextRun({
                                        text: 'No events match the current report view.',
                                        size: 28,
                                        font: 'Arial',
                                        color: THEME.muted,
                                    }),
                                ],
                            }),
                        ],
                    }),
                ],
            }),
        ],
    });

export const exportReportToDoc = async ({
    report,
    viewerUserId,
    viewerDisplayName,
    groupBy,
    organizationLogoUrl,
    fileNameSuffix,
}: ReportExportContext): Promise<boolean> => {
    try {
        const createdByLabel = resolveReportExportCreatedByLabel(
            report,
            viewerUserId,
            viewerDisplayName,
        );
        const summaryGrid = buildReportExportSummaryGrid(
            report,
            createdByLabel,
        );
        const promptGroups = groupReportEventsForExport(report.events, groupBy);
        const generatedAt = formatDate(Math.floor(Date.now() / 1000));
        const logoPng =
            await loadReportExportLogoPngForDocx(organizationLogoUrl);

        const children: (Paragraph | Table)[] = [
            ...buildTitleBlock(report),
            buildSummaryPanelTable(summaryGrid),
            buildDividerParagraph(),
        ];

        if (report.events.length === 0) {
            children.push(buildEmptyEventsPanel());
        }

        for (const group of promptGroups) {
            if (group.showHeader) {
                children.push(
                    ...buildPromptIntroParagraphs(
                        group.headingLabel,
                        group.promptText,
                        group.events,
                    ),
                );
            }

            for (const event of group.events) {
                children.push(await buildPdfLikeEventRow(event, groupBy));
                children.push(
                    new Paragraph({
                        spacing: { after: 200 },
                        children: [new TextRun({ text: '\u00a0', size: 2 })],
                    }),
                );
            }
        }

        const wordDocument = new Document({
            title: report.name,
            sections: [
                {
                    headers: {
                        default: buildReportDocumentHeader(logoPng),
                    },
                    footers: {
                        default: buildReportDocumentFooter(generatedAt),
                    },
                    properties: {
                        page: {
                            size: {
                                width: WORD_EXPORT_PAGE_TWIP.width,
                                height: WORD_EXPORT_PAGE_TWIP.height,
                            },
                            margin: {
                                top: WORD_EXPORT_PAGE_TWIP.marginTop,
                                right: WORD_EXPORT_PAGE_TWIP.marginRight,
                                bottom: WORD_EXPORT_PAGE_TWIP.marginBottom,
                                left: WORD_EXPORT_PAGE_TWIP.marginLeft,
                                header: WORD_EXPORT_PAGE_TWIP.header,
                                footer: WORD_EXPORT_PAGE_TWIP.footer,
                            },
                        },
                    },
                    children,
                },
            ],
        });

        const blob = await Packer.toBlob(wordDocument);
        triggerBlobDownload(
            blob,
            `${toSafeExportFileName(report.name, 'report')}${fileNameSuffix}.docx`,
        );

        return true;
    } catch {
        return false;
    }
};
